import { uuidv7 } from "uuidv7"

import type { File } from "~/models/polyschema/File"
import type { FileCommentRequestDatagram } from "~/models/polyschema/FileCommentRequestDatagram"

import { parseFile } from "~/utils/flatbuffers/file"
import { last } from "~/utils/helpers"

import type { FileBuf } from "../generated/file"

import { EntityMimeTypeId, mimeTypeString, PropertyId, PropertySchema, PropertyStatus } from "../entity"
import { InterfaceSelection } from "../entity/InterfaceSelection"
import { withStatic } from "../entity/StaticEntity"
import { sendFileCommentRequests } from "../handlers/file/comment"
import { sendFileUpdateRequests } from "../handlers/file/update"
import { BaseFile } from "./BaseFile"

export class StaticFile extends withStatic<
  FileProperty,
  File,
typeof BaseFile
>(BaseFile) {
  _inner: File

  constructor(file: File) {
    super()
    this._inner = markRaw(file)
  }

  static from(file?: File) {
    if (file)
      return new StaticFile(file)
  }

  static fromBuf(buf: FileBuf): StaticFile {
    return new StaticFile(parseFile(buf))
  }

  /**
   * NOTE: FileEvent is just an alias for File (for now) but this may change
   */
  static fromEvent(event: FileEvent): StaticFile {
    return new StaticFile(event)
  }

  /**
   * When we don't have enough info about a file to type it. Should only be used for, e.g.
   * moving files or other clear placeholder tasks. Should not be used to create a file
   * in the UI as this file will lack other necessary properties
   */
  static blank(id: string) {
    const selection = InterfaceSelection.nothing().inner()
    return new StaticFile({ id, properties: {}, selection })
  }

  inner(): Readonly<File> {
    return this._inner
  }

  _entity(): File {
    return this._inner
  }

  propertyOrVirtualPropertyValue(id: PropertyId): EntityPropertyValue | undefined {
    if (id === PropertyId.FILE_VIRTUAL_SELF_ROLE)
      return { type: PropertySchema.RoleUuidValue, role: this.impliedSelfRole()._role.toString() }
    else if (id === PropertyId.FILE_VIRTUAL_ADDED_AT)
      return this.addedAt()
        ? { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: this.addedAt()!.getTime() }
        : undefined
    else if ([
      PropertyId.FILE_VIRTUAL_PRE_HASH,
      PropertyId.FILE_VIRTUAL_MEDIA_TYPE,
      PropertyId.FILE_VIRTUAL_FULL_TEXT,
      PropertyId.FILE_VIRTUAL_EVERYTHING,
      PropertyId.FILE_VIRTUAL_CONTENTS,
      PropertyId.FILE_VIRTUAL_EMBEDDINGS,
      PropertyId.FILE_VIRTUAL_PAGE_EMBEDDINGS,
      PropertyId.FILE_VIRTUAL_PREVIEW,
      PropertyId.FILE_VIRTUAL_DIMENSIONS,
      PropertyId.FILE_VIRTUAL_TAGS,
      PropertyId.FILE_VIRTUAL_PATH,
      PropertyId.FILE_VIRTUAL_SEARCH_RESULT,
    ].includes(id)) {
      console.error("Cannot get virtual property value of this property id", id)
      return
    }
    return this.property(id)?.value
  }

  asSlug() {
    if (this.isUserHomeFolder())
      return "/home"
    if (this.isUserArchiveFolder())
      return "/archive"
    if (this.isFolder())
      return `/folder/id/${asShortId(this.id)}`
    return `/file/id/${asShortId(this.id)}`
  }

  path<T = StaticFile>(): T[] | undefined {
    if (this._inner.path === undefined)
      return
    return this._inner.path.map(f => new StaticFile(f)) as any
  }

  asPath(): StaticFile[] {
    if (this._inner.path === undefined)
      return [this]
    return [this, ...this._inner.path.map(f => new StaticFile(f))]
  }

  /**
   * Checks if the file ids of this path match the other file. Does not check the properties
   * of files on the paths.
   */
  pathMatches(other: StaticFile): boolean {
    if (other._inner.path === undefined || this._inner.path === undefined)
      throw new Error("Paths can't be undefined")

    // check each element
    if (other._inner.path && this._inner.path) {
      for (const [otherFileId, myFileId] of zip(other._inner.path, this._inner.path)) {
        if (otherFileId.id !== myFileId.id)
          return false
      }
    }
    return true
  }

  setPath(path: StaticFile[]) {
    this._inner.path = path.map(f => f._inner)
  }

  setPathRaw(path: File[]) {
    this._inner.path = path
  }

  rootFolder(): StaticFile | undefined {
    if (this._inner.path === undefined)
      return
    if (!this._inner.path.length)
      return new StaticFile(this._inner)
    return new StaticFile(last(this._inner.path)!)
  }

  setUri(s3BlobUri: string) {
    if (!this._inner.properties[PropertyId.FILE_URI])
      throw new Error("No uri property")
    this._inner.properties[PropertyId.FILE_URI].value = {
      type: PropertySchema.S3BlobUriValue,
      s3BlobUri,
    }
  }

  getArchiveFolderFromPath(): StaticFile | undefined {
    if (this.isUserHomeFolder() || this.isSharedDriveHome())
      return
    if (this.isUserArchiveFolder() || this.isSharedDriveArchive())
      return this as StaticFile
    const root = this.rootFolder()
    if (!root)
      throw new Error("No way to know archive dir for file")
    if (root.id !== this.id)
      return root.getArchiveFolderFromPath()
  }

  getHomeFolderFromPath(): StaticFile | undefined {
    if (this.isUserArchiveFolder() || this.isSharedDriveArchive())
      return
    if (this.isUserHomeFolder() || this.isSharedDriveHome())
      return this as StaticFile
    const root = this.rootFolder()
    if (!root)
      throw new Error("No way to know home dir for file")
    if (root.id !== this.id)
      return root.getHomeFolderFromPath()
  }

  allowsMovingToDestination = (destination: StaticFile) => {
    const fileRole = this.impliedSelfRole()
    const destinationRole = destination.impliedSelfRole()
    const isCurrentlyArchived = isDefined(this.getArchiveFolderFromPath())
    const willBeArchived = isDefined(destination.getArchiveFolderFromPath())
    if (isCurrentlyArchived && !fileRole.canMoveFromArchive())
      return false
    if (willBeArchived && !fileRole.canMoveToArchive())
      return false
    return fileRole.canMove() && destinationRole.canUpload()
  }

  allowsUpdatingMetadata = () => {
    const fileRole = this.impliedSelfRole()
    return fileRole.canUpdateMetadata()
  }

  allowsDownloading = () => {
    const fileRole = this.impliedSelfRole()
    return fileRole.canDownload()
  }

  allowsCommenting = () => {
    const fileRole = this.impliedSelfRole()
    return fileRole.canComment()
  }

  /**
   * Merges two interface file's based on their properties. The "newer" one doesn't have
   * to be newer as such, but we take its properties and path by default, so it should
   * be the more recently retrieved file if possible.
   *
   * Rules:
   * 1. If the current file does not have a property, it will be added from the newer file
   * 2. If the incoming file has a newer epoch id, we know that has to be more up to date
   * 3. If the current file has a property but it is not current (pending) and the newer
   *    file has a current property, it will replace the current file's property with
   *    the newer one (even if the version and/or timestamp is lower!). This allows for
   *    rolling back failed updates.
   * 4. If the version is higher we take the newer one
   * 5. If the timestamp is higher we take the newer one
   */
  override mergeWithNewer<T = StaticFile>(later?: T): boolean {
    const newer = later as StaticFile | undefined
    if (!newer)
      return false
    let changed = super.mergeWithNewer(later)
    if (isDefined(newer._inner.path)) {
      this._inner.path = newer._inner.path
      changed = true
    }
    return changed
  }

  updateProperty = <Req = FileUpdateRequestDatagram>(
    propertyId: PropertyId,
    newVal: EntityPropertyValue,
    deleted?: boolean,
    unversioned?: boolean,
    timestamp?: string,
  ): {
    updated: StaticFile
    request: Req
  } => {
    return this.updateProperties([{
      propertyId,
      newVal,
      deleted,
      unversioned,
      timestamp,
    }]) as {
      updated: StaticFile
      request: Req
    }
  }

  /**
   * TODO: add the abstract version of this to reactive-entity once all entity types have
   * a multi-property update method.
   */
  updateProperties = (requests: {
    propertyId: PropertyId
    newVal: EntityPropertyValue
    deleted?: boolean
    unversioned?: boolean
    timestamp?: string
  }[]): {
    updated: StaticFile
    request: FileUpdateRequestDatagram
  } => {
    const { state: myself } = useSelf()
    const { isOffline } = useCache()

    if (!this.id)
      throw new Error("Cannot update file without a file id, might be still loading")
    if (!myself.value.user)
      throw new Error("Anon users can't update files")
    if (requests.length === 0)
      throw new Error("Cannot update file properties with an empty request list")

    const timestamp = requests[0].timestamp ?? uuidv7()
    const newFile = StaticFile.blank(this.id)
    newFile.setModifiedAt(timestamp, myself.value.user.id)

    for (const { propertyId, newVal, deleted, unversioned } of requests) {
      let version = 0
      const schema = newVal.type

      // If we're offline we don't increment the version (everything is pending). If the property is
      // unversioned, it doesn't get an incremented property
      const prop = this.inner().properties?.[propertyId]

      if (prop && !isOffline() && !unversioned) {
        version = prop.version + 1
      }
      else if (prop && isOffline()) {
        version = prop.version
      }

      newFile.addProperty({
        id: newFile.id,
        propertyId,
        version,
        status: PropertyStatus.StatusPending,
        timestamp,
        value: newVal,
        deleted,
        schema,
        author: myself.value.user.id,
      })
    }

    const requestId = generateWorkerRequestId()
    const request: FileUpdateRequestDatagram = {
      requestId,
      file: newFile.inner(),
      offline: false,
    }
    sendFileUpdateRequests([request])

    // Update ourselves as soon as possible
    this.mergeWithNewer(newFile)

    return {
      updated: this,
      request,
    }
  }

  /**
   * Updates the file's accessed-at timestamp.
   * @param timestamp an optional timestamp, defaults to now
   * @returns the request
   */
  setFileAccessedIfAllowed = (
    timestamp?: string,
  ): {
    updated: StaticFile
    request: FileUpdateRequestDatagram
  } | undefined => {
    const { state: myself } = useSelf()

    if (!myself.value.user)
      return
    if (!this.allowsUpdatingMetadata()) {
      console.debug("Skipping file accessed property update due to permissions")
      return
    }

    const newFile = StaticFile.blank(this.id)
    newFile.setAccessedAt(timestamp = timestamp ?? uuidv7(), myself.value.user.id)

    const request = {
      requestId: generateWorkerRequestId(),
      file: newFile.inner(),
      offline: false,
    }
    sendFileUpdateRequests([request])

    this.mergeWithNewer(newFile)

    return { updated: this, request }
  }

  postComment = (markdown: string) => {
    const { state: myself } = useSelf()

    if (!myself.value.user) {
      console.error("Anon users can't post comments")
      return
    }
    if (!this.allowsCommenting()) {
      console.error("Skipping file comment post due to permissions")
      return
    }
    const newFile = StaticFile.blank(this.id)
    newFile.setNewFileComment({
      markdown,
      timestamp: uuidv7(),
      originalAuthor: myself.value.user.id,
    })
    const request: FileCommentRequestDatagram = {
      requestId: generateWorkerRequestId(),
      file: newFile.inner(),
      offline: false,
    }
    sendFileCommentRequests([request])

    this.mergeWithNewer(newFile)

    return { updated: this, request }
  }

  /**
   * TODO: for searches, we want to have a different comparison, so we will need to
   * handle that case separately, only for the search view.
   *
   * NOTE: retrieving the `createdAt()`, etc. causes a loss of precision since the
   * millisecond timestamp values might be the same. Instead, we compare the raw
   * uuids, which have additional precision built in.
   *
   * NOTE: for virtual added at, we use the file parent id, since we want to know when
   * the last moved time was.
   *
   * @param a the first string
   * @param b the second string
   * @param sort property to sort by
   * @param desc whether to sort in descending order
   * @param grouped whether we are inside a grouping (changes some sort rules)
   * @returns -1 if a < b, 0 if a === b, 1 if a > b
   */
  static compare(a: StaticFile, b: StaticFile, sort: PropertyId, desc: boolean, grouped: boolean) {
    // if a field is null, it should always be last.
    function cmp<T>(a: T | undefined, b: T | undefined, fn: (a: T, b: T) => number) {
      if (!isDefined(a) && !isDefined(b))
        return 0
      if (!isDefined(a))
        return 1
      if (!isDefined(b))
        return -1

      if (desc)
        return fn(b, a)
      return fn(a, b)
    }
    switch (sort) {
      case PropertyId.FILE_NAME: return grouped
        ? cmp(a.name(), b.name(), (a, b) => a.localeCompare(b))
        : cmp(a.name(), b.name(), (a, b) => naturalCompare(a, b, true))
      case PropertyId.FILE_SIZE: return cmp(
        a.size(),
        b.size(),
        (a, b) => a - b,
      )
      case PropertyId.FILE_CREATED_AT: return cmp(
        a.property(PropertyId.FILE_CREATED_AT)?.timestamp,
        b.property(PropertyId.FILE_CREATED_AT)?.timestamp,
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_MODIFIED_AT: return cmp(
        a.property(PropertyId.FILE_MODIFIED_AT)?.timestamp,
        b.property(PropertyId.FILE_MODIFIED_AT)?.timestamp,
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_ACCESSED_AT: return cmp(
        a.property(PropertyId.FILE_ACCESSED_AT)?.timestamp,
        b.property(PropertyId.FILE_ACCESSED_AT)?.timestamp,
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_VIRTUAL_ADDED_AT: return cmp(
        a.property(PropertyId.FILE_PARENT_ID)?.timestamp,
        b.property(PropertyId.FILE_PARENT_ID)?.timestamp,
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.MEDIA_WIDTH_PX: return cmp(
        a.mediaWidthPx(),
        b.mediaWidthPx(),
        (a, b) => a - b,
      )
      case PropertyId.MEDIA_HEIGHT_PX: return cmp(
        a.mediaHeightPx(),
        b.mediaHeightPx(),
        (a, b) => a - b,
      )
      case PropertyId.MEDIA_DURATION_MS: return cmp(
        a.mediaDurationMs(),
        b.mediaDurationMs(),
        (a, b) => a - b,
      )
      case PropertyId.MEDIA_PAGES: return cmp(
        a.mediaPages(),
        b.mediaPages(),
        (a, b) => a - b,
      )
      case PropertyId.MEDIA_WIDTH_CM: return cmp(
        a.mediaWidthCm(),
        b.mediaWidthCm(),
        (a, b) => a - b,
      )
      case PropertyId.MEDIA_HEIGHT_CM: return cmp(
        a.mediaHeightCm(),
        b.mediaHeightCm(),
        (a, b) => a - b,
      )
      case PropertyId.FILE_RATING: return cmp(
        a.rating(),
        b.rating(),
        (a, b) => a - b,
      )
      case PropertyId.FILE_MIME_TYPE: return cmp(
        maybe(mimeTypeString)(a.mimeType()),
        maybe(mimeTypeString)(b.mimeType()),
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_VIRTUAL_MEDIA_TYPE: return cmp(
        a.mediaType(),
        b.mediaType(),
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_NOTES: return cmp(
        a.notes(),
        b.notes(),
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_GENERATION_PROMPT: return cmp(
        a.generationPrompt(),
        b.generationPrompt(),
        (a, b) => a.localeCompare(b),
      )
      case PropertyId.FILE_GENERATION_NEGATIVE_PROMPT: return cmp(
        a.generationNegativePrompt(),
        b.generationNegativePrompt(),
        (a, b) => a.localeCompare(b),
      )
    }
    throw new Error(`Cannot sort by property ${sort}`)
  }

  static newFolder({
    id,
    userId,
    parentId,
    name,
  }: { id: string, userId: string, parentId: string, name: string }) {
    const timestamp = uuidv7()
    const file = StaticFile.blank(id)
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_NAME,
      version: 0,
      schema: PropertySchema.NonEmptyStringValue,
      value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: name },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_PARENT_ID,
      version: 0,
      schema: PropertySchema.FileIdValue,
      value: { type: PropertySchema.FileIdValue, fileId: parentId },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_CREATED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: Date.now() },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_MODIFIED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: Date.now() },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_ACCESSED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: Date.now() },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_MIME_TYPE,
      version: 0,
      schema: PropertySchema.MimeTypeValue,
      value: { type: PropertySchema.MimeTypeValue, mimeType: { value: EntityMimeTypeId.InodeDirectory } },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    return file
  }

  static async newLink({
    id,
    userId,
    parentId,
    url,
  }: { id: string, userId: string, parentId: string, url: string }) {
    const timestamp = uuidv7()
    const utcTimestampMillis = Date.now()
    const file = StaticFile.blank(id)
    const { getYoutubeVideoId } = useCache()
    const isYoutubeVideo = !!(await getYoutubeVideoId(url))

    file.addProperty({
      id,
      propertyId: PropertyId.FILE_NAME,
      version: 0,
      schema: PropertySchema.NonEmptyStringValue,
      value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: url },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.LINK_DESTINATION_URL,
      version: 0,
      schema: PropertySchema.WebUrlValue,
      value: { type: PropertySchema.WebUrlValue, url },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_PARENT_ID,
      version: 0,
      schema: PropertySchema.FileIdValue,
      value: { type: PropertySchema.FileIdValue, fileId: parentId },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_CREATED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_MODIFIED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_ACCESSED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_MIME_TYPE,
      version: 0,
      schema: PropertySchema.MimeTypeValue,
      value: { type: PropertySchema.MimeTypeValue, mimeType: isYoutubeVideo ? { value: EntityMimeTypeId.VendorYoutubeVideo } : { value: EntityMimeTypeId.TextUriList } },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    return file
  }

  static mockImage({
    id,
    userId,
    parentId,
    uri,
    name,
    size,
    createdAt,
    mimeType,
    width,
    height,
    parameters,
  }: { id: string, userId: string, parentId: string, uri: string, name: string, size: number, createdAt: number, mimeType: EntityMimeType, width: number, height: number, parameters?: GenerationMetadata }) {
    const timestamp = uuidv7()
    const file = StaticFile.blank(id)

    file.addProperty({
      id,
      propertyId: PropertyId.FILE_URI,
      version: 0,
      schema: PropertySchema.S3BlobUriValue,
      value: { type: PropertySchema.S3BlobUriValue, s3BlobUri: uri },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_NAME,
      version: 0,
      schema: PropertySchema.NonEmptyStringValue,
      value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: name },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_SIZE,
      version: 0,
      schema: PropertySchema.SizeBytesValue,
      value: { type: PropertySchema.SizeBytesValue, sizeBytes: size },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_PARENT_ID,
      version: 0,
      schema: PropertySchema.FileIdValue,
      value: { type: PropertySchema.FileIdValue, fileId: parentId },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_CREATED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: createdAt },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_MODIFIED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: createdAt },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_ACCESSED_AT,
      version: 0,
      schema: PropertySchema.UtcTimestampMillisValue,
      value: { type: PropertySchema.UtcTimestampMillisValue, utcTimestampMillis: createdAt },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.FILE_MIME_TYPE,
      version: 0,
      schema: PropertySchema.MimeTypeValue,
      value: { type: PropertySchema.MimeTypeValue, mimeType },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    // // Image Properties
    file.addProperty({
      id,
      propertyId: PropertyId.MEDIA_WIDTH_PX,
      version: 0,
      schema: PropertySchema.PositiveIntegerValue,
      value: { type: PropertySchema.PositiveIntegerValue, positiveInteger: width },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })
    file.addProperty({
      id,
      propertyId: PropertyId.MEDIA_HEIGHT_PX,
      version: 0,
      schema: PropertySchema.PositiveIntegerValue,
      value: { type: PropertySchema.PositiveIntegerValue, positiveInteger: height },
      timestamp,
      status: PropertyStatus.StatusPending,
      author: userId,
    })

    if (parameters?.prompt)
      file.addProperty({
        id,
        propertyId: PropertyId.FILE_GENERATION_PROMPT,
        version: 0,
        schema: PropertySchema.NonEmptyStringValue,
        value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: parameters.prompt.toString() },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: userId,
      })
    if (parameters?.negative_prompt)
      file.addProperty({
        id,
        propertyId: PropertyId.FILE_GENERATION_NEGATIVE_PROMPT,
        version: 0,
        schema: PropertySchema.NonEmptyStringValue,
        value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: parameters.negative_prompt.toString() },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: userId,
      })
    if (parameters?.program_used)
      file.addProperty({
        id,
        propertyId: PropertyId.FILE_GENERATION_SOURCE,
        version: 0,
        schema: PropertySchema.NonEmptyStringValue,
        value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: parameters.program_used.toString() },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: userId,
      })
    if (parameters?.seed !== undefined)
      file.addProperty({
        id,
        propertyId: PropertyId.FILE_GENERATION_SEED,
        version: 0,
        schema: PropertySchema.AnyIntegerValue,
        value: { type: PropertySchema.AnyIntegerValue, anyInteger: Number.parseInt(parameters.seed) },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: userId,
      })
    if (parameters?.model)
      file.addProperty({
        id,
        propertyId: PropertyId.FILE_GENERATION_MODEL,
        version: 0,
        schema: PropertySchema.NonEmptyStringValue,
        value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: parameters.model.toString() },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: userId,
      })
    if (parameters?.model_used_version)
      file.addProperty({
        id,
        propertyId: PropertyId.FILE_GENERATION_MODEL_VERSION,
        version: 0,
        schema: PropertySchema.NonEmptyStringValue,
        value: { type: PropertySchema.NonEmptyStringValue, nonEmptyString: parameters.model_used_version.toString() },
        timestamp,
        status: PropertyStatus.StatusPending,
        author: userId,
      })

    return file
  }
}
